// ============================================
// 📄 GERADOR DE PDF — TCLE (Termo de Consentimento Livre e Esclarecido)
// ============================================
// Termo de consentimento para uso de IA de apoio à decisão, voltado ao
// PACIENTE. Pronto para impressão (window.print → salvar PDF), com o logo do
// ambulatório no topo e o nome do paciente já preenchido — o paciente apenas
// assina e data (coleta física incremental, no retorno; B113/B211/B212).
//
// 🔒 FONTE ÚNICA DO TEXTO: docs/tcle-e-revisao-humana.md (Parte A, v1.2). Ao
// atualizar o termo lá, refletir AQUI (e na constante VERSAO_TCLE). Só a Parte A
// (voltada ao paciente) entra no documento assinado — a "Nota para o responsável"
// e a Parte B (manual do profissional) NÃO integram o termo.
//
// O médico responsável e o serviço são FIXOS (controlador dos dados = Leonardo
// Toledo Mota, CRM-RO 3793 — governança §1.6), independentes do usuário logado.

import { Paciente } from "../../../types";
import { abrirPDFEmNovaJanela } from "../../prescricoes/services/gerarPDF";
import logoBase64 from "../../prescricoes/services/logoBase64";

// Dados fixos do termo (espelham o cabeçalho da Parte A do documento canônico)
export const SERVICO_TCLE = "Ambulatório de Transplante Hepático de Rondônia";
export const MEDICO_RESPONSAVEL_TCLE = "Dr. Leonardo Toledo Mota — CRM-RO 3793";
export const CONTATO_TCLE = "leohepato@gmail.com";
export const VERSAO_TCLE = "1.2";

const esc = (s: string | undefined): string =>
  (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/**
 * Monta o HTML completo (A4, pronto-para-impressão) do TCLE com o nome do
 * paciente preenchido. Função PURA (sem efeitos) — separada de gerarPDFTCLE
 * para ser testável sem window/navigator.
 */
export function montarHTMLTCLE(paciente: Paciente): string {
  const nome = esc(paciente.nome) || "&nbsp;";
  const cpf = esc(paciente.cpf);

  return `
<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <title>TCLE (uso de IA) - ${esc(paciente.nome)}</title>
  <style>
    @page { size: A4; margin: 18mm 16mm 20mm 16mm; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Helvetica Neue', 'Segoe UI', Arial, sans-serif;
      color: #1a1a1a;
      background: white;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      font-size: 12.5px;
      line-height: 1.5;
    }
    .page { max-width: 210mm; margin: 0 auto; }
    .header {
      display: flex; align-items: center; gap: 16px;
      padding-bottom: 12px; border-bottom: 3px solid #2d6a2e; margin-bottom: 14px;
    }
    .header-logo { width: 78px; height: 78px; flex-shrink: 0; }
    .header-logo img { width: 100%; height: 100%; object-fit: contain; }
    .header-text h1 {
      font-size: 17px; font-weight: 700; color: #2d6a2e;
      text-transform: uppercase; margin-bottom: 4px;
    }
    .header-text p { font-size: 12px; color: #444; }
    .titulo {
      text-align: center; font-size: 15px; font-weight: 700;
      text-transform: uppercase; margin: 10px 0 12px; line-height: 1.3;
    }
    .ident { font-size: 12px; margin-bottom: 14px; }
    .ident p { margin-bottom: 2px; }
    .ident strong { font-weight: 700; }
    h2 {
      font-size: 13px; font-weight: 700; color: #2d6a2e;
      margin: 12px 0 5px; break-after: avoid;
    }
    p { margin-bottom: 7px; text-align: justify; }
    ul { margin: 0 0 7px 18px; }
    li { margin-bottom: 4px; text-align: justify; }
    .declaracao {
      margin: 8px 0 14px; padding: 10px 12px;
      background: #f4f8f4; border-left: 3px solid #2d6a2e; font-weight: 600;
    }
    table.assinatura {
      width: 100%; border-collapse: collapse; margin-top: 6px;
      break-inside: avoid;
    }
    table.assinatura td {
      border: 1px solid #888; padding: 9px 10px; font-size: 12.5px;
      vertical-align: middle;
    }
    table.assinatura td.rot {
      width: 32%; background: #f0f0f0; font-weight: 700;
    }
    table.assinatura tr.alta td { height: 46px; }
    .rodape {
      margin-top: 14px; padding-top: 8px; border-top: 1px solid #ccc;
      font-size: 10.5px; color: #777; text-align: center;
    }
    @media print { body { margin: 0; } .no-print { display: none !important; } }
  </style>
</head>
<body>
  <!-- BOTÕES (não aparecem na impressão) -->
  <div class="no-print" style="text-align: center; padding: 12px; background: #f0f0f0;">
    <button onclick="window.print()" style="padding: 10px 32px; background: #2563eb; color: white; border: none; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; margin-right: 8px;">
      🖨️ Imprimir
    </button>
    <button onclick="window.close()" style="padding: 10px 32px; background: #6b7280; color: white; border: none; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer;">
      ✕ Fechar
    </button>
  </div>

  <div class="page">
    <div class="header">
      <div class="header-logo"><img src="${logoBase64}" alt="Logo" /></div>
      <div class="header-text">
        <h1>${SERVICO_TCLE}</h1>
      </div>
    </div>

    <div class="titulo">Termo de Consentimento Livre e Esclarecido (TCLE)<br/>para uso de Inteligência Artificial de apoio à decisão</div>

    <div class="ident">
      <p><strong>Serviço:</strong> ${SERVICO_TCLE}</p>
      <p><strong>Médico responsável:</strong> ${MEDICO_RESPONSAVEL_TCLE}</p>
      <p><strong>Contato (direitos do titular):</strong> ${CONTATO_TCLE}</p>
    </div>

    <h2>1. O que é este documento</h2>
    <p>Este termo explica, de forma clara, como um programa de <strong>inteligência artificial (IA) de apoio à decisão</strong> é usado no seu atendimento e pede o seu consentimento para esse uso. Você pode tirar dúvidas a qualquer momento e a sua decisão é livre.</p>

    <h2>2. Como a IA é usada no seu atendimento</h2>
    <ul>
      <li>A IA é uma <strong>ferramenta de apoio ao seu médico</strong> — ela ajuda a organizar informações, sugerir análises e redigir laudos.</li>
      <li><strong>A IA não decide nada sozinha sobre o seu tratamento.</strong> Toda informação gerada por ela é <strong>revisada, editada e aprovada pelo seu médico</strong> antes de ter qualquer efeito no seu cuidado.</li>
      <li>Nenhuma conduta (diagnóstico, prescrição, pedido de exame) é tomada automaticamente pelo computador.</li>
    </ul>

    <h2>3. O seu direito à revisão humana (Art. 20 da LGPD)</h2>
    <p>A lei garante que decisões que afetam você não sejam tomadas <strong>apenas</strong> por um sistema automático. No hepato.app isso já é a regra de funcionamento: <strong>há sempre um médico revisando</strong> a saída da IA antes de qualquer registro no seu prontuário. Se você quiser, pode solicitar ao médico esclarecimentos sobre como uma informação apoiada por IA foi avaliada.</p>

    <h2>4. Quais dados são tratados e para quê</h2>
    <ul>
      <li><strong>Dados usados:</strong> seus dados de identificação e seus dados de saúde (prontuário, exames, sorologias), estritamente para a finalidade de <strong>apoio à sua assistência clínica</strong> em hepatologia/transplante.</li>
      <li><strong>Minimização:</strong> sempre que possível, os dados enviados à IA são <strong>minimizados/anonimizados</strong>.</li>
    </ul>

    <h2>5. Compartilhamento e transferência internacional</h2>
    <p>Para gerar a análise de IA e armazenar arquivos, alguns dados podem ser processados por <strong>serviços em nuvem localizados no exterior (EUA)</strong>, sob cláusulas contratuais de proteção e <strong>sem uso dos seus dados para treinar os modelos</strong>. O banco de dados principal do prontuário fica <strong>no Brasil</strong> (São Paulo). Sempre que possível, os dados enviados à IA são <strong>anonimizados</strong> antes do envio; quando for necessário enviar dados que identifiquem você, isso ocorre <strong>com o seu consentimento</strong> (LGPD Art. 33, VII).</p>

    <h2>6. Envio de documentos por SMS e WhatsApp</h2>
    <ul>
      <li>Quando o seu médico assinar digitalmente um documento (por exemplo, uma <strong>receita</strong> ou um <strong>pedido de exames</strong>), você poderá receber, <strong>no número de celular cadastrado</strong>, uma mensagem por <strong>SMS e/ou WhatsApp</strong> com um <strong>link</strong> para acessar o documento.</li>
      <li>A mensagem traz <strong>apenas o link</strong> — <strong>nenhum dado de saúde</strong> (remédio, diagnóstico ou tipo de documento) é escrito na mensagem.</li>
      <li>Para abrir o documento, é preciso informar os <strong>4 últimos dígitos do seu celular</strong> (uma segunda conferência que protege os seus dados de saúde caso o celular seja visto por outra pessoa).</li>
      <li>O envio do link usa um <strong>serviço de mensagens contratado</strong>, que trata apenas o seu número de telefone para a entrega, sob obrigação de sigilo.</li>
      <li>Esse envio é uma <strong>conveniência</strong> e <strong>não substitui</strong> a via impressa nem a validade legal da assinatura. Você pode <strong>recusar</strong> ou <strong>cancelar</strong> esse recebimento a qualquer momento, sem prejuízo ao seu atendimento.</li>
    </ul>

    <h2>7. Os seus direitos</h2>
    <p>Você pode, a qualquer momento: confirmar que há tratamento de dados, acessar seus dados, corrigir informações, pedir esclarecimentos, e <strong>revogar este consentimento</strong>. A revogação suspende novos usos de IA no seu atendimento e <strong>não prejudica</strong> a continuidade do seu cuidado médico nem apaga registros que a lei obriga a manter (o prontuário é guardado por 20 anos — Resolução CFM 1.821/2007).</p>

    <h2>8. Consentimento</h2>
    <div class="declaracao">Declaro que li (ou me foi lido) este termo, que tive a oportunidade de tirar dúvidas e que <strong>concordo com o uso da IA de apoio à decisão</strong> no meu atendimento e <strong>autorizo o envio de documentos por SMS/WhatsApp</strong> ao meu celular (Seção 6), nos termos acima.</div>

    <table class="assinatura">
      <tr><td class="rot">Nome do paciente</td><td>${nome}</td></tr>
      ${cpf ? `<tr><td class="rot">CPF</td><td>${cpf}</td></tr>` : ""}
      <tr class="alta"><td class="rot">Assinatura</td><td>&nbsp;</td></tr>
      <tr><td class="rot">Data</td><td>______ / ______ / __________</td></tr>
      <tr><td class="rot">Médico responsável</td><td>${MEDICO_RESPONSAVEL_TCLE}</td></tr>
    </table>

    <div class="rodape">
      hepato.app — TCLE de uso de IA, versão ${VERSAO_TCLE} (LGPD Arts. 7º/11, 18, 20 e 33; Resolução CFM 2.454/2026).
      O termo assinado é arquivado no prontuário do paciente.
    </div>
  </div>
</body>
</html>
  `;
}

/**
 * Abre o TCLE do paciente em nova janela, pronto para imprimir / salvar como PDF.
 */
export function gerarPDFTCLE(paciente: Paciente): void {
  abrirPDFEmNovaJanela(montarHTMLTCLE(paciente));
}
